import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resolveMediaObject } from '../lib/bookingMedia';
import { buildResaleOgMeta } from '../lib/resaleOgMeta';
import { useReportHeadMeta } from '../lib/useReportHeadMeta';
import {
  MARKET_CONDITION_LABELS,
  RESALE_SECTION_KEYS,
  RESALE_SECTION_LABELS,
  scoreBand,
} from '../lib/resaleScore';
import { publicResaleReportUrl } from '../lib/resaleReportUrl';
import { supabase } from '../lib/supabase';
import '../styles/public-resale-report.css';

const COPY = {
  en: {
    title: 'Certified pre-purchase inspection',
    vehicle: 'Vehicle details',
    seller: 'Seller contact',
    overall: 'Overall score',
    market: 'Market condition',
    repairs: 'Immediate repair requirements',
    maintenance: 'Expected maintenance',
    media: 'Inspection media',
    summary: 'Inspector summary',
    verify: 'Verify this report',
    disclaimer:
      'This inspection reflects the vehicle condition at the time of inspection only. It is not a warranty or ownership guarantee.',
    notFound: 'Report not found or link has expired.',
    loading: 'Loading inspection report…',
    plate: 'Plate',
    vin: 'VIN',
    mileage: 'Mileage',
    color: 'Color',
    fuel: 'Fuel',
    transmission: 'Transmission',
    engine: 'Engine',
    phone: 'Phone',
    city: 'City',
    inspector: 'Inspector',
    urgent: 'Urgent',
    soon: 'Soon',
    months: 'months',
    close: 'Close',
    viewFullScreen: 'View full screen',
    status: {
      good: 'Good',
      fair: 'Fair',
      poor: 'Poor',
      critical: 'Critical',
    },
    sections: RESALE_SECTION_LABELS,
  },
  ar: {
    title: 'فحص معتمد قبل الشراء',
    vehicle: 'تفاصيل السيارة',
    seller: 'بيانات البائع',
    overall: 'التقييم الإجمالي',
    market: 'الحالة في السوق',
    repairs: 'إصلاحات مطلوبة فوراً',
    maintenance: 'صيانة متوقعة',
    media: 'صور وفيديو الفحص',
    summary: 'ملخص الفاحص',
    verify: 'التحقق من التقرير',
    disclaimer:
      'يعكس هذا الفحص حالة السيارة وقت الفحص فقط. لا يُعد ضماناً أو تأكيداً للملكية.',
    notFound: 'التقرير غير موجود أو انتهت صلاحية الرابط.',
    loading: 'جاري تحميل تقرير الفحص…',
    plate: 'اللوحة',
    vin: 'رقم الهيكل',
    mileage: 'العداد',
    color: 'اللون',
    fuel: 'الوقود',
    transmission: 'ناقل الحركة',
    engine: 'المحرك',
    phone: 'الهاتف',
    city: 'المدينة',
    inspector: 'الفاحص',
    urgent: 'عاجل',
    soon: 'قريباً',
    months: 'شهر',
    close: 'إغلاق',
    viewFullScreen: 'عرض بملء الشاشة',
    status: {
      good: 'جيد',
      fair: 'مقبول',
      poor: 'ضعيف',
      critical: 'حرج',
    },
    sections: {
      engine: 'المحرك',
      transmission: 'ناقل الحركة',
      chassis: 'الهيكل',
      paint_thickness: 'سماكة الطلاء',
      computer_diagnosis: 'تشخيص الكمبيوتر',
      suspension: 'نظام التعليق',
      tires: 'الإطارات',
      air_conditioning: 'التكييف',
      electrical: 'الأنظمة الكهربائية',
    },
  },
};

function scoreRingColor(score) {
  if (score >= 85) return '#34c759';
  if (score >= 70) return '#0a84ff';
  if (score >= 55) return '#ff9f0a';
  return '#ff453a';
}

function ScoreRing({ score, label }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <div className="pub-score-ring" aria-label={`${label}: ${score}`}>
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <circle className="pub-score-ring-bg" cx="60" cy="60" r={radius} />
        <circle
          className="pub-score-ring-fill"
          cx="60"
          cy="60"
          r={radius}
          stroke={scoreRingColor(score)}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="pub-score-ring-text">
        <strong>{score}</strong>
        <span>{label}</span>
      </div>
    </div>
  );
}

function PublicMedia({ item, onOpenImage, viewLabel }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    resolveMediaObject(item).then((resolved) => {
      if (alive) setUrl(resolved.url);
    });
    return () => {
      alive = false;
    };
  }, [item]);

  if (!url) {
    return <div className="pub-report-media loading" aria-hidden="true" />;
  }

  if (item.kind === 'video') {
    return (
      <video className="pub-report-media" controls src={url}>
        <track kind="captions" />
      </video>
    );
  }

  return (
    <button
      type="button"
      className="pub-report-media-btn"
      onClick={() => onOpenImage?.({ url, caption: item.caption || '' })}
      aria-label={viewLabel}
    >
      <img className="pub-report-media" src={url} alt={item.caption || ''} />
    </button>
  );
}

function MediaLightbox({ image, onClose, closeLabel }) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  if (!image) return null;

  return (
    <div className="pub-lightbox" role="dialog" aria-modal="true" aria-label={image.caption || 'Inspection photo'}>
      <button type="button" className="pub-lightbox-backdrop" onClick={onClose} aria-label={closeLabel} />
      <div className="pub-lightbox-content">
        <button type="button" className="pub-lightbox-close" onClick={onClose} aria-label={closeLabel}>
          ×
        </button>
        <img className="pub-lightbox-image" src={image.url} alt={image.caption || ''} />
        {image.caption ? <p className="pub-lightbox-caption">{image.caption}</p> : null}
      </div>
    </div>
  );
}

function SpecRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="pub-report-spec">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function PublicResaleReportPage() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [lang, setLang] = useState('en');
  const [lightboxImage, setLightboxImage] = useState(null);
  const t = COPY[lang];

  useEffect(() => {
    let alive = true;
    async function load() {
      const { data, error: rpcError } = await supabase.rpc(
        'get_public_resale_inspection',
        { p_token: token }
      );
      if (!alive) return;
      if (rpcError) {
        setError(rpcError.message);
        return;
      }
      setReport(data);
    }
    load();
    return () => {
      alive = false;
    };
  }, [token]);

  const vehicleTitle = useMemo(() => {
    if (!report) return '';
    return [report.brand, report.model, report.year].filter(Boolean).join(' ');
  }, [report]);

  const ogMeta = useMemo(() => {
    if (!report || !token) return null;
    return {
      ...buildResaleOgMeta(report, token),
      lang: lang === 'ar' ? 'ar' : 'en',
      locale: lang === 'ar' ? 'ar_EG' : 'en_US',
    };
  }, [report, token, lang]);

  useReportHeadMeta(ogMeta);

  if (error) {
    return (
      <div className="pub-report-state" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div>
          <p className="error">{t.notFound}</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="pub-report-state" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
        <div>
          <div className="pub-report-spinner" aria-hidden="true" />
          <p>{t.loading}</p>
        </div>
      </div>
    );
  }

  const business = report.business || {};
  const branch = report.branch || {};
  const shareUrl = publicResaleReportUrl(token);
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareUrl)}`;
  const marketLabel =
    MARKET_CONDITION_LABELS[report.market_condition] || report.market_condition;
  const brandInitial = (business.brand_name || 'W').trim().charAt(0).toUpperCase();
  const finalizedDate = report.finalized_at
    ? new Date(report.finalized_at).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <div className="pub-report" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="pub-report-shell">
        <header className="pub-report-topbar">
          <div className="pub-report-brand">
            {business.logo_url ? (
              <img className="pub-report-logo" src={business.logo_url} alt="" />
            ) : (
              <div className="pub-report-logo-fallback" aria-hidden="true">
                {brandInitial}
              </div>
            )}
            <div className="pub-report-brand-copy">
              <strong>{business.brand_name || 'BW Station'}</strong>
              <span>
                {[branch.name, branch.address].filter(Boolean).join(' · ') ||
                  business.address ||
                  business.legal_name}
              </span>
            </div>
          </div>
          <div className="pub-report-lang" role="group" aria-label="Language">
            <button
              type="button"
              className={lang === 'en' ? 'active' : ''}
              onClick={() => setLang('en')}
            >
              <span className="pub-report-lang-long">English</span>
              <span className="pub-report-lang-short">EN</span>
            </button>
            <button
              type="button"
              className={lang === 'ar' ? 'active' : ''}
              onClick={() => setLang('ar')}
            >
              عربي
            </button>
          </div>
        </header>

        <section className="pub-report-hero">
          <span className="pub-report-kicker">{t.title}</span>
          <h1>{vehicleTitle || report.report_number}</h1>
          <p className="pub-report-meta">
            {report.report_number}
            {finalizedDate ? ` · ${finalizedDate}` : ''}
            {report.inspector_name ? ` · ${t.inspector}: ${report.inspector_name}` : ''}
          </p>
          <div className="pub-report-hero-body">
            <ScoreRing score={report.overall_score ?? 0} label={t.overall} />
            <div className={`pub-market-badge ${report.market_condition || ''}`}>
              <span>{t.market}</span>
              <em>{marketLabel}</em>
            </div>
          </div>
        </section>

        <div className="pub-report-grid-2">
          <section className="pub-report-card">
            <h2>{t.vehicle}</h2>
            <div className="pub-report-specs">
              <SpecRow label={t.plate} value={report.plate_number} />
              <SpecRow label={t.vin} value={report.vin} />
              <SpecRow
                label={t.mileage}
                value={
                  report.mileage_km
                    ? `${Number(report.mileage_km).toLocaleString()} km`
                    : null
                }
              />
              <SpecRow label={t.color} value={report.color} />
              <SpecRow label={t.fuel} value={report.fuel_type} />
              <SpecRow label={t.transmission} value={report.transmission} />
              <SpecRow label={t.engine} value={report.engine_size} />
            </div>
          </section>

          <section className="pub-report-card">
            <h2>{t.seller}</h2>
            <div className="pub-report-seller">
              <strong>{report.seller_name}</strong>
              {report.seller_city ? (
                <p>
                  {t.city}: {report.seller_city}
                </p>
              ) : null}
              <p>
                <a href={`tel:${report.seller_phone}`}>
                  {t.phone}: {report.seller_phone}
                </a>
              </p>
            </div>
          </section>
        </div>

        {report.summary_notes ? (
          <section className="pub-report-card">
            <h2>{t.summary}</h2>
            <p className="pub-report-summary">{report.summary_notes}</p>
          </section>
        ) : null}

        <h2 className="pub-report-section-title">{t.overall}</h2>
        <div className="pub-report-sections">
          {RESALE_SECTION_KEYS.map((key) => {
            const section = report.sections?.[key] || {};
            const status = (section.status || 'good').toLowerCase();
            const score = section.score ?? '—';
            const band = scoreBand(Number(section.score) || 0);
            return (
              <article key={key} className="pub-report-section">
                <div className="pub-report-section-head">
                  <strong>{t.sections[key] || RESALE_SECTION_LABELS[key]}</strong>
                  <span className={`pub-report-status ${status}`}>
                    {t.status[status] || section.status}
                  </span>
                </div>
                <div className={`pub-report-scorebar ${band}`}>
                  <span style={{ width: `${Math.max(0, Math.min(100, Number(section.score) || 0))}%` }} />
                </div>
                <div className="pub-report-section-score">
                  {typeof score === 'number' ? `${score} / 100` : score}
                </div>
                {section.notes ? <p>{section.notes}</p> : null}
              </article>
            );
          })}
        </div>

        {(report.immediate_repairs || []).length ? (
          <section className="pub-report-card">
            <h2>{t.repairs}</h2>
            <ul className="pub-report-lines">
              {report.immediate_repairs.map((row) => (
                <li key={row.title} className={row.priority === 'urgent' ? 'urgent' : ''}>
                  <strong>{row.title}</strong>
                  <span>
                    {row.priority === 'urgent' ? t.urgent : t.soon}
                    {row.cost_min_egp != null || row.cost_max_egp != null
                      ? ` · EGP ${row.cost_min_egp ?? '—'}–${row.cost_max_egp ?? '—'}`
                      : ''}
                  </span>
                  {row.notes ? <p>{row.notes}</p> : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {(report.expected_maintenance || []).length ? (
          <section className="pub-report-card">
            <h2>{t.maintenance}</h2>
            <ul className="pub-report-lines">
              {report.expected_maintenance.map((row) => (
                <li key={row.item}>
                  <strong>{row.item}</strong>
                  <span>
                    {row.horizon_months ? `${row.horizon_months} ${t.months}` : ''}
                    {row.cost_egp != null ? ` · EGP ${row.cost_egp}` : ''}
                  </span>
                  {row.notes ? <p>{row.notes}</p> : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {(report.inspection_media || []).length ? (
          <section className="pub-report-card">
            <h2>{t.media}</h2>
            <div className="pub-report-media-grid">
              {report.inspection_media.map((item) => (
                <PublicMedia
                  key={item.path}
                  item={item}
                  viewLabel={t.viewFullScreen}
                  onOpenImage={setLightboxImage}
                />
              ))}
            </div>
          </section>
        ) : null}

        {lightboxImage ? (
          <MediaLightbox
            image={lightboxImage}
            closeLabel={t.close}
            onClose={() => setLightboxImage(null)}
          />
        ) : null}

        <footer className="pub-report-verify">
          <h2>{t.verify}</h2>
          <img src={qrUrl} alt="QR verification" />
          <p className="pub-report-code">{report.verification_code}</p>
          <p className="pub-report-disclaimer">{t.disclaimer}</p>
          {business.phone_primary || branch.phone || business.support_email ? (
            <p className="pub-report-contact">
              {[business.phone_primary || branch.phone, business.support_email, business.website]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
        </footer>
      </div>
    </div>
  );
}
